<script lang="ts">
  import type { schema } from "@editor/schema";
  import Rich from "../utils/Rich.svelte";
  import Block from "../Block.svelte";
  import PhotoField from "../form/PhotoField.svelte";
  import { getNewsletterStores } from "../../newsletterStores";
  import { isEditor, photoToCTA, withDefaults, LinkContent, fancyPicClassIfNoCTA } from "../utils";
  import { isEmpty } from "../blocksConfig";
  import { isRTL } from "sbelt/text";

  export let block: schema.ParagraphBlock;
  const { design, content, ctx } = getNewsletterStores();

  function photoUpdated(p: schema.Photo | undefined) {
    content.updateById<schema.ParagraphBlock>(block._id, (b) => {
      b.picture = p;
    });
  }

  // get defaults
  $: [b, isDefault] = withDefaults(block);
</script>

<Block
  bind:block
  on:click
  on:delete
  on:duplicate
  let:layout
  layouts={["-", "side-l", "side-r"]}
  let:isEmpty
  showEmpty
  preventClicks={!isEmpty}
  let:logged_out>
  <div>
    <!-- Photo -->
    {#if layout === "side-l" || (layout === "side-r" && (block.picture || isEditor()))}
      <div
        role="presentation"
        on:click|stopPropagation
        class="group pointer-events-auto relative mb-4 overflow-hidden rounded sm:mb-1 print:mb-1"
        class:sm:w-48={(b.picture && b.picture.legacy_thumb_url) || isEditor()}
        class:print:w-48={(b.picture && b.picture.legacy_thumb_url) || isEditor()}
        class:sm:float-left={layout === "side-l"}
        class:sm:mr-6={layout === "side-l" && ((b.picture && b.picture.legacy_thumb_url) || isEditor())}
        class:sm:float-right={layout === "side-r"}
        class:sm:ml-4={layout === "side-r"}
        class:print:float-left={layout === "side-l"}
        class:print:mr-6={layout === "side-l" && ((b.picture && b.picture.legacy_thumb_url) || isEditor())}
        class:print:float-right={layout === "side-r"}
        class:print:ml-4={layout === "side-r"}>
        {#if b.picture && b.picture.legacy_thumb_url && !isEditor()}
          <LinkContent
            let:isLink
            cta={photoToCTA(b.picture)}
            class="skip-tracking image-container relative block h-full w-full {fancyPicClassIfNoCTA(b.picture)}"
            data-title={b.picture.title}
            data-img-full={b.picture.legacy_full_url}
            type="paragraph-image"
            linkText={b.picture.alt_text}
            aria-label={b.picture.alt_text}>
            <img
              aria-hidden={isLink}
              alt={b.picture.alt_text || "User uploaded image"}
              title={b.picture.alt_text}
              src={b.picture.legacy_thumb_url}
              class="max-h-full max-w-full rounded bg-transparent object-contain" />
          </LinkContent>
          <div
            class="absolute left-0 right-0 top-0 flex items-center justify-end gap-x-1.5 p-1.5 opacity-0 group-hover:opacity-100"
            aria-hidden="true">
            {#if b.picture.cta?.url}
              <LinkContent
                cta={photoToCTA(b.picture)}
                class="skip-tracking material-icons notranslate relative flex rounded bg-nxgray-700 bg-opacity-80 p-1 text-lg text-white"
                data-title={b.picture.title}
                data-img-full={b.picture.legacy_full_url}
                type="paragraph-image"
                linkText={b.picture.alt_text}
                aria-hidden="true"
                tabindex="-1">
                open_in_new
              </LinkContent>
            {/if}
            <a
              class="fancy-pic skip-tracking material-icons notranslate focusable rounded bg-nxgray-700 bg-opacity-80 p-1 text-lg text-white"
              href={b.picture.legacy_full_url}
              aria-hidden="true"
              tabindex="-1"
              >zoom_out_map
            </a>
          </div>
        {:else if isEditor()}
          <PhotoField
            value={block.picture}
            handoffId="side-photo-{block._id}"
            on:upload_done={(e) => photoUpdated(e.detail)}
            on:clear={(e) => photoUpdated(undefined)}
            on:logged_out={logged_out}
            mode="crop"
            showErrorMessage />
        {/if}
      </div>
    {/if}

    <!-- Title -->
    {#if b.title}
      <h4
        class="mb-2 text-18 font-bold leading-tight text-gray-600"
        dir={isRTL(b.title) ? "rtl" : "ltr"}
        class:opacity-50={isDefault(b.title)}
        data-field-key="title"
        class:text-center={b.align === "center"}
        style="color:{$design.background.dark ? '#fff' : isDefault(b.title) ? '#505060' : $design.color['global.text']}">
        {b.title}
      </h4>
    {/if}

    {#if b.content}
      <div
        class="text-15"
        class:opacity-50={isDefault(b.content)}
        style="color:{$design.background.dark ? '#fff' : isDefault(b.content) ? '#505060' : $design.color['global.text']}"
        data-field-key="content">
        <Rich content={b.content} context={{ type: "paragraph" }} />
      </div>
    {/if}

    <div class="clear-both" />
  </div>
</Block>

<style>
  .fancy-pic {
    position: relative !important;
    display: flex !important;
  }
</style>
